import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);

    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /**
   * Keep the drawer cart row count in sync with the cart.
   * @param {CustomEvent<{ data?: { itemCount?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const itemCount = event.detail?.data?.itemCount;

    if (typeof itemCount !== 'number') return;

    this.#renderCartCount(itemCount);
  };

  /**
   * @param {number} count
   */
  #renderCartCount(count) {
    for (const element of this.querySelectorAll('[data-drawer-cart-count]')) {
      element.textContent = String(count);
    }
  }

  /**
   * Reads the count rendered in the header cart bubble, which stays up to date
   * even though the drawer markup is skipped during section morphs.
   */
  #syncCartCount() {
    const bubble = document.querySelector('cart-icon .cart-bubble__text-count');
    const count = parseInt(bubble?.textContent?.trim() ?? '', 10);

    if (!Number.isNaN(count)) this.#renderCartCount(count);
  }

  /**
   * Close the menu drawer and open the cart drawer instead, when one is present.
   * Falls back to following the cart link.
   * @param {Event} event
   */
  openCart(event) {
    const cartDrawer = /** @type {HTMLElement & { open?: () => void }} */ (
      document.querySelector('cart-drawer-component')
    );

    if (typeof cartDrawer?.open !== 'function') return;

    event.preventDefault();
    this.close();

    setTimeout(() => cartDrawer.open(), 100);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    this.#syncCartCount();
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * The circular category rail at the top of the menu drawer. Renders a scroll
 * indicator, since mobile browsers hide native scrollbars.
 */
class DrawerHighlights extends HTMLElement {
  /** @type {HTMLElement | null} */
  #list = null;
  /** @type {HTMLElement | null} */
  #thumb = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    this.#list = this.querySelector('[data-highlights-list]');
    this.#thumb = this.querySelector('[data-highlights-thumb]');

    if (!this.#list || !this.#thumb) return;

    this.#list.addEventListener('scroll', this.#update, { passive: true });
    this.#resizeObserver = new ResizeObserver(this.#update);
    this.#resizeObserver.observe(this.#list);
    this.#update();
  }

  disconnectedCallback() {
    this.#list?.removeEventListener('scroll', this.#update);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  #update = () => {
    const list = this.#list;
    const thumb = this.#thumb;

    if (!list || !thumb) return;

    const scrollable = list.scrollWidth - list.clientWidth;

    if (scrollable <= 1) {
      this.setAttribute('data-no-scroll', '');
      return;
    }

    this.removeAttribute('data-no-scroll');

    const ratio = list.clientWidth / list.scrollWidth;
    const progress = list.scrollLeft / scrollable;

    thumb.style.width = `${ratio * 100}%`;
    thumb.style.transform = `translateX(${progress * (1 / ratio - 1) * 100}%)`;
  };
}

if (!customElements.get('drawer-highlights')) {
  customElements.define('drawer-highlights', DrawerHighlights);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
